import React from 'react';

import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { REVIEW_FORM_KEYS, useSelectedReviewForm } from 'reducers/reviewFormSlice';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { StyledTooltip } from 'styles/common';
import { truncate } from 'utils/lib';

import { objectFlip } from 'constants/helper';

import { TagColored } from 'uiToolkitV1/Tag';

import { useCompetencyFormData } from '../CompetencyForm/hooks/dataHooks';
import { useGoalFormData } from '../GoalForm/hooks/dataHooks';
import { useOverallQuestionsFormData } from '../OverallQuestionsForm/hooks/dataHooks';
import REVIEW_FORM_TYPES from '../Sidebar/common/REVIEW_FORM_TYPES';

export const getHardcodedText = (rating) => {
  if (rating < 1) {
    return 'Unsatisfactory';
  }
  if (rating <= 2) {
    return 'Below expectations';
  }
  if (rating <= 3) {
    return 'Consistently meets expectations';
  }
  if (rating <= 4) {
    return 'Exceeds expectations';
  }
  if (rating <= 5) {
    return 'Outstanding performance';
  }
  return '-';
};

export const getRatingText = (rating, ratingMap) => {
  if (!ratingMap) return '-';
  const reversedRatingMap = objectFlip(ratingMap);
  const isAnyValNaN = Object.keys(reversedRatingMap).some((key) => Number.isNaN(key));
  if (isAnyValNaN) return '-';

  const ratingTextKey = Object.keys(reversedRatingMap).find((key) => rating <= Number(key));
  if (ratingTextKey) {
    return reversedRatingMap[ratingTextKey];
  }
  return '-';
};

// if reviewForm is passed then returns score of passed reviewForm else reads selectedReviewForm from redux
export const OverallFormScoreText = ({ reviewFormKey = null, isTextOnly = false }) => {
  const { moduleName } = useOrganisationSettings();

  const { employeeId, providerId, reviewId } = useReviewFormParams();

  const selectedReviewForm = useSelectedReviewForm();
  const reviewForm = reviewFormKey || selectedReviewForm;

  const { data: goalFormData, isLoading: isLoadingGoalFormData } = useGoalFormData({
    employeeId,
    providerId,
    reviewId,
  });
  const { data: competencyFormData, isLoading: isLoadingCompetencyFormData } = useCompetencyFormData({
    employeeId,
    providerId,
    reviewId,
  });
  const { data: overallFormData, isLoading: isLoadingOverallFormData } = useOverallQuestionsFormData({
    employeeId,
    providerId,
    reviewId,
  });

  const { averageGoalRatingText } = goalFormData ?? {};

  const { averageCompetencyRatingText } = competencyFormData ?? {};

  const { averageQNARatingText } = overallFormData ?? {};

  let ratingText = '';

  if (reviewForm === REVIEW_FORM_KEYS.GOAL && averageGoalRatingText && averageGoalRatingText !== undefined) {
    ratingText = averageGoalRatingText;
  } else if (
    reviewForm === REVIEW_FORM_KEYS.COMPETENCY &&
    averageCompetencyRatingText &&
    averageCompetencyRatingText !== undefined
  ) {
    ratingText = averageCompetencyRatingText;
  } else if (reviewForm === REVIEW_FORM_KEYS.QNA && averageQNARatingText && averageQNARatingText !== undefined) {
    ratingText = averageQNARatingText;
  }

  if (!ratingText || ratingText === 'NA') {
    return null;
  }

  if (!selectedReviewForm || isLoadingGoalFormData || isLoadingCompetencyFormData || isLoadingOverallFormData) {
    return null;
  }
  const updatedTitle =
    REVIEW_FORM_TYPES[selectedReviewForm].titlePlural === 'Goals'
      ? `${moduleName}s`
      : REVIEW_FORM_TYPES[selectedReviewForm].titlePlural;

  if (isTextOnly) {
    return (
      <StyledTooltip title={ratingText}>
        <span>Score: {truncate(ratingText, 30)}</span>
      </StyledTooltip>
    );
  }
  return (
    <TagColored color="black">
      {updatedTitle} score: {ratingText}
    </TagColored>
  );
};

export const OverallFormScore = () => {
  const selectedReviewForm = useSelectedReviewForm();

  if (!selectedReviewForm) return null;

  return <OverallFormScoreText />;
};
